import { useEffect, useRef, useState } from 'react';

import api from '../../utils/api';
import { btnStyle, inputStyle, labelStyle } from './styles';

const boxStyle = {
  marginTop: 8,
  padding: 10,
  border: '1px solid var(--c-border-strong)',
  borderRadius: 6,
  background: 'var(--c-bg-subtle)',
};

const linkStyle = {
  background: 'none',
  border: 'none',
  color: 'var(--c-primary-fg)',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
  fontSize: 12,
};

const warnStyle = { marginBottom: 6, fontSize: 12, color: 'var(--c-warning-text-2-fg)' };

/**
 * Adding a place the region dictionary does not have yet.
 *
 * Shown from the wizard and from the detail modal, which used to hold a copy
 * each -- the same fifty lines twice, and a fix to one of them was a fix to
 * one of them.
 *
 * The whole point of this form is to NOT create a duplicate region, so it
 * searches while the name is typed and puts what it finds in front of the
 * person before they can save. If they save anyway and the server recognises
 * the name (409), that is not treated as a failure: the existing region is
 * selected, exactly as if they had picked it from the list, and the form stays
 * open with a notice saying which one they got. A duplicate region silently
 * created would split one place's collections across two names forever.
 *
 * `seqRef` is the caller's: the same counter its region search and its GPS
 * lookup bump. A POST that lands after the widget was closed or the person
 * typed something else must not select anything, so it checks the counter it
 * took on the way out.
 */
export default function AddRegionForm({ t, initialQuery, seqRef, onPicked, onClose, style }) {
  const [nameHe, setNameHe] = useState(initialQuery);
  const [nameEn, setNameEn] = useState('');
  const [type, setType] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [similar, setSimilar] = useState([]);
  const [duplicateNotice, setDuplicateNotice] = useState('');
  const debounceRef = useRef(null);

  // The first token of the prefilled name, checked the moment the form opens.
  // Typing never reaches this case: somebody who accepts the prefill as-is
  // would otherwise see no warning at all. The first token alone also catches
  // a longer name that contains a shorter existing one, which a plain search
  // for the whole string misses.
  useEffect(() => {
    const firstToken = initialQuery.trim().split(/\s+/)[0];
    if (firstToken.length < 2) return;
    let live = true;
    api.get(`/regions/search?q=${encodeURIComponent(firstToken)}`)
      .then(res => { if (live) setSimilar(res.data || []); })
      .catch(() => {});
    return () => { live = false; };
    // Deliberately once, on open: later checks come from typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const onNameHeChange = (e) => {
    const val = e.target.value;
    setNameHe(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setSimilar([]); return; }
    debounceRef.current = setTimeout(() => {
      api.get(`/regions/search?q=${encodeURIComponent(val.trim())}`)
        .then(res => setSimilar(res.data || []))
        .catch(() => setSimilar([]));
    }, 300);
  };

  const take = (loc) => { onPicked(loc); onClose(); };

  const valid = !!nameHe.trim() && !!type;

  const submit = () => {
    if (!valid) return;
    setSaving(true);
    setError('');
    setDuplicateNotice('');
    const seq = ++seqRef.current;
    api.post('/regions', {
      name_he: nameHe.trim(),
      name_en: nameEn.trim() || null,
      location_type: type,
    })
      .then(res => {
        if (seq !== seqRef.current) return; // widget closed/reopened while this was in flight
        take(res.data);
      })
      .catch(err => {
        if (seq !== seqRef.current) return;
        if (err.response?.status === 409 && err.response?.data?.location_id) {
          // Already there. Select it and say so, rather than refuse.
          onPicked(err.response.data);
          setDuplicateNotice(t('step2.addRegionDuplicateNotice'));
        } else {
          setError(err.response?.data?.error || t('step2.addRegionFailed'));
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ ...boxStyle, ...style }}>
      <label style={{ ...labelStyle, marginBottom: 6 }}>
        {t('step2.addRegionNameHe')}
        <input value={nameHe} onChange={onNameHeChange} dir="rtl" style={inputStyle} title={t('tip.regionNameHe')} />
      </label>
      <label style={{ ...labelStyle, marginBottom: 6 }}>
        {t('step2.addRegionNameEn')}
        <input value={nameEn} onChange={e => setNameEn(e.target.value)} dir="ltr" style={inputStyle} title={t('tip.regionNameEn')} />
      </label>
      <label style={{ ...labelStyle, marginBottom: 6 }}>
        {t('step2.addRegionType')}
        <select value={type} onChange={e => setType(e.target.value)} style={inputStyle} title={t('tip.regionType')}>
          <option value="">{t('common:selectPlaceholder')}</option>
          <option value="zone">{t('step2.addRegionTypeZone')}</option>
          <option value="reserve">{t('step2.addRegionTypeReserve')}</option>
          <option value="city">{t('step2.addRegionTypeCity')}</option>
          <option value="forest">{t('step2.addRegionTypeForest')}</option>
        </select>
      </label>
      {similar.length > 0 && (
        <div style={warnStyle}>
          {t('step2.addRegionSimilarWarning')}
          <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
            {similar.map(loc => (
              <li key={loc.location_id}>
                <button type="button" onClick={() => take(loc)} style={linkStyle} title={t('tip.pickSimilarRegion')}>
                  {loc.name_he}{loc.name_en ? ` (${loc.name_en})` : ''}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {duplicateNotice && (
        <p style={{ color: 'var(--c-warning-text-2-fg)', fontSize: 12, margin: '0 0 6px' }}>{duplicateNotice}</p>
      )}
      {error && <p style={{ color: 'red', fontSize: 12, margin: '0 0 6px' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={submit} disabled={saving || !valid} title={t('tip.saveRegion')}
          style={btnStyle(saving || !valid ? 'var(--c-text-faint)' : 'var(--c-primary)')}>
          {saving ? t('common:saving') : t('step2.addRegionSave')}
        </button>
        <button type="button" onClick={onClose} style={btnStyle('var(--c-text-muted)')} title={t('tip.cancelAddForm')}>
          {t('common:cancel')}
        </button>
      </div>
    </div>
  );
}
